import React, { useEffect, useState } from "react";
import { Box, Card, Container, Fab, GridListTile, TablePagination, Typography, withStyles, ImageList } from "@material-ui/core";
import { Add as AddIcon, Event as EventIcon, ViewList as ViewListIcon, FilterList as FilterIcon } from "@material-ui/icons";
import moment from "moment";
import { Club } from "api/clubs";
import { Court } from "api/courts";
import { Booking, BookingType, fetchCompanyBookings } from "api/bookings";
import { useIdleTimer } from "react-idle-timer";
import { UserProfile } from "api/user";
import { Paginated } from "utils/api";
import BookingCell from "./BookingCell";
import CompanyBookingCell from "./CompanyBookingCell";
import styles from "./CalendarView.module.scss";
import BookingDetailDialog from "./BookingDetailDialog";
import CreateBookingDialog from "./CreateBookingDialog";
import i18n from "./i18n";
import CalendarFilterDialog from "./CalendarFilterDialog";

// prop types
interface CalendarViewProps {
    bookings: Booking[],
    club: Club | null,
    date: string,
    embedded?: boolean,
    showMenu: boolean,
    userProfile: UserProfile | null,
    onFetchBookings: (clubId: string, date: string) => void,
    onSetDate: (date: string) => void,
    onShowLoginDialog: () => void,
}

type Filters = Parameters<typeof fetchCompanyBookings>[0];
const LIMIT = 5;
// calendar view
const CalendarView = ({ bookings, club, date, embedded, showMenu, userProfile, onFetchBookings, onSetDate, onShowLoginDialog }: CalendarViewProps) => {
    const cellHeight = parseInt(styles.cellHeight, 10);
    const cellWidth = parseInt(styles.cellWidth, 10);
    const courts = [...(club?.courts || [])].sort((c1, c2) => c1.courtNumber - c2.courtNumber);

    // ui state
    const [currentTime, setCurrentTime] = useState(moment().toISOString());
    const [isIdle, setIdle] = useState(false);
    const [showBookingDetailDialog, setShowBookingDetailDialog] = useState<string | null>(null);
    const [showCreateBookingDialog, setShowCreateBookingDialog] = useState<{ court?: string, date?: string, template?: Booking } | null>(null);
    const [isCalendarView, setIsCalendarView] = useState(true);
    const [companyBookings, setCompanyBookings] = useState<Paginated<Booking>>({ data: [], totalCount: 0 });
    const [query, setQuery] = useState({ rowsPerPage: LIMIT, page: 0, clubId: club?.id });
    const [reset, setReset] = useState(false);
    const [showCalendarFilterDialog, setShowCalendarFilterDialog] = useState(false);
    const [companyExclusiveBookingFilters, setCompanyExclusiveBookingFilters] = useState<Filters>({});

    const userRole = userProfile?.memberships.find((m) => m.club.id === club?.id)?.userRole;
    const isCompany = club?.capabilities.includes("company");

    // current time updater
    useEffect(() => {
        if (isCalendarView) {
            const interval = setInterval(() => setCurrentTime(moment().toISOString()), 2000);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [isCalendarView]);

    // booking cell clicked
    const onBookingCellClick = (e: React.MouseEvent, id: string) => {
        if (e.getModifierState("Meta") && userProfile?.userTypes?.includes("root-dev")) {
            navigator.clipboard.writeText(id); // DEV ONLY: copy booking id if cmd key is held down
        } else {
            setShowBookingDetailDialog(id);
        }
    };

    // set current day & scroll to current hour
    const showCurrentDayAndHour = () => {
        onSetDate(moment().toISOString());
        window.scrollTo({ top: cellHeight * (moment().hour() - earliestHour), behavior: "smooth" });
    };

    // idle hook
    useIdleTimer({
        debounce: 250,
        timeout: 1000 * 60,
        onActive: () => setIdle(false),
        onIdle: () => setIdle(true),
    });
    useEffect(() => { if (isIdle && !userProfile) showCurrentDayAndHour(); }, [isIdle, userProfile]); // eslint-disable-line

    // timer hook -> set current day & scroll to current hour
    useEffect(() => {
        const secondsSinceHour = moment().diff(moment().startOf("hour"), "seconds");
        if (secondsSinceHour < 3 && isIdle && !userProfile) showCurrentDayAndHour();
    }, [currentTime, isIdle, userProfile]); // eslint-disable-line

    // get open and close hour of club, create hours array
    let earliestHour = Math.floor(Math.min(...courts.map((court) => court.openTimeMins)) / 60);
    if (!isFinite(earliestHour)) earliestHour = 0;
    let latestHour = Math.ceil(Math.max(...courts.map((court) => court.closeTimeMins)) / 60);
    if (!isFinite(latestHour)) latestHour = 24;
    const times = Array.from(Array(latestHour - earliestHour).keys())
        .map((hour) => moment({ hour: hour + earliestHour }).format("H:mm"));

    const getCompanyBookings = async (clubId: string) => {
        fetchCompanyBookings({ clubId, fromDate: moment(date).startOf("day").toISOString(), filter: companyExclusiveBookingFilters?.filter, query: companyExclusiveBookingFilters?.query, isVirtualEvent: companyExclusiveBookingFilters?.isVirtualEvent, limit: query.rowsPerPage, offset: query.rowsPerPage * query.page })
            .then((result) => {
                setCompanyBookings(result);
            });
    };

    // fetch bookings
    useEffect(() => {
        if (club) {
            onFetchBookings(club.id, date);
            if (isCompany) {
                getCompanyBookings(club!.id);
            }
        }
    }, [club, date]); // eslint-disable-line

    // fetch bookings
    useEffect(() => {
        if (club) {
            if (isCompany) {
                getCompanyBookings(club!.id);
                setIsCalendarView(false);
            } else {
                setIsCalendarView(true);
            }
        }
    }, [club]); // eslint-disable-line

    // query changed -> fetch data
    useEffect(() => { if (club) getCompanyBookings(club!.id); }, [club, query, reset]); // eslint-disable-line

    // render not available cells (court closed)
    const NotAvailableCells = ({ court }: { court: Court }) => (
        <>
            <Card
                elevation={0}
                style={{
                    background: styles.bookingBlockedColor,
                    height: (court.openTimeMins / 60 - earliestHour) * cellHeight,
                    position: "absolute",
                    transform: "translate(3px, 0)",
                    width: cellWidth - 6,
                }}
            />
            <Card
                elevation={0}
                style={{
                    background: styles.bookingBlockedColor,
                    height: (latestHour - (court.closeTimeMins / 60)) * cellHeight,
                    position: "absolute",
                    transform: `translate(3px, ${(court.closeTimeMins / 60 - earliestHour) * cellHeight}px)`,
                    width: cellWidth - 6,
                }}
            />
        </>
    );

    // experimental: find next free time slot
    const findFreeSlot = (court: string, fromDate: string) => {
        let nextSlot = fromDate;
        while (moment(nextSlot).hour() < club?.courts.find((c) => c.id === court)?.closeTimeMins! / 60) {
            const conflicting = bookings.find((b) => b.court.id === court && b.startDate <= nextSlot && b.endDate > nextSlot && b.bookingType !== BookingType.Meetup); // eslint-disable-line
            if (conflicting) {
                nextSlot = conflicting.endDate;
            } else {
                return nextSlot;
            }
        }
        return fromDate; // fallback, none found
    };

    // render
    const currentTimeMoment = moment(currentTime);
    const currentTimeMomentIgnoreDST = currentTimeMoment.clone().set({ dayOfYear: 1 });
    const minutesSinceMidnight = currentTimeMomentIgnoreDST.diff(currentTimeMomentIgnoreDST.clone().startOf("day"), "minutes");
    const OverflowTile = withStyles({ tile: { overflow: "visible", wordBreak: "break-word" } })(GridListTile);
    return (
        <>
            {isCompany && (
                <>
                    {!isCalendarView && (
                        <>
                            <Fab
                                color="primary"
                                size="medium"
                                className={styles.filterIcon}
                                onClick={() => {
                                    setShowCalendarFilterDialog(true);
                                }}
                            >
                                <FilterIcon />
                            </Fab>
                            <Fab
                                color="primary"
                                size="medium"
                                className={styles.calenderListIcon}
                                onClick={() => setIsCalendarView(!isCalendarView)}
                            >
                                <EventIcon />
                            </Fab>
                        </>
                    )}
                    {isCalendarView && (
                        <Fab
                            color="primary"
                            size="medium"
                            className={styles.calenderListIcon}
                            onClick={() => setIsCalendarView(!isCalendarView)}
                        >
                            <ViewListIcon />
                        </Fab>
                    )}
                </>
            )}

            {/* bookings */}
            {!isCalendarView && (
                <>
                    {/* header */}
                    <Box className={styles.header}>
                        <Typography variant="h6">
                            {i18n.listViewTitle}
                        </Typography>
                    </Box>
                    {(companyBookings.data || []).length === 0 && (
                        <Box className={styles.noBokingArea}>
                            {i18n.noRecordFound}
                        </Box>
                    )}
                    {companyBookings && (
                        <Container maxWidth="sm" className={styles.containerMargin}>
                            <ImageList cols={1} rowHeight="auto" gap={8}>
                                {companyBookings.data.map((b) => (
                                    <OverflowTile key={b.id}>
                                        {club && (
                                            <CompanyBookingCell
                                                booking={b}
                                                isStandardUser={userRole === "standard"}
                                                key={b.id}
                                                timeOffset={earliestHour}
                                                onClick={(e) => onBookingCellClick(e, b.id)}
                                                club={club}
                                                getCompanyBookings={getCompanyBookings}
                                            />
                                        )}
                                    </OverflowTile>
                                ))}
                            </ImageList>
                        </Container>
                    )}
                    {/* pagination */}
                    <TablePagination
                        component="div"
                        className={styles.pagination}
                        count={companyBookings?.totalCount}
                        page={query.page}
                        rowsPerPage={query.rowsPerPage}
                        rowsPerPageOptions={[5, 10, 15, 20]}
                        onPageChange={(e, page) => setQuery({ ...query, page })}
                        onRowsPerPageChange={(e) => setQuery({ ...query, rowsPerPage: Number(e.target.value), page: 0 })}
                    />
                </>
            )}
            {isCalendarView && (
                <Box className={styles.root} style={{ pointerEvents: embedded ? "none" : "auto" }}>
                    {/* hours */}
                    <Box className={showMenu ? styles.hoursContainerWithMenu : styles.hoursContainer}>
                        <Box className={styles.courtName}>
                            {/* current time (if today) */}
                            {currentTimeMoment.isSame(moment(date), "day") && (
                                <span className={styles.currentTimeText}>{currentTimeMoment.format("H:mm")}</span>
                            )}
                        </Box>
                        {times.map((hour) => (
                            <Box className={styles.hour} key={hour}>{hour}</Box>
                        ))}
                    </Box>

                    {/* courts */}
                    {courts.map((court) => (
                        <Box className={styles.courtContainer} key={court.id}>
                            {/* header */}
                            <Box className={styles.courtName}>
                                {court.courtName}
                            </Box>

                            {/* not available */}
                            <NotAvailableCells court={court} />

                            {/* current time (if today) */}
                            {currentTimeMoment.isSame(moment(date), "day") && (
                                <Box
                                    className={styles.currentTimeLine}
                                    style={{ transform: `translate(0px, ${(minutesSinceMidnight / 60 - earliestHour) * cellHeight}px)` }}
                                />
                            )}

                            {/* bookings */}
                            {bookings.filter((b) => b.court.id === court.id).map((b) => (
                                <BookingCell
                                    booking={b}
                                    isStandardUser={userRole === "standard"}
                                    key={b.id}
                                    timeOffset={earliestHour}
                                    onClick={(e) => onBookingCellClick(e, b.id)}
                                />
                            ))}

                            {/* empty cells */}
                            {times.map((hour) => (
                                <Box
                                    className={styles.emptyCell}
                                    key={hour}
                                    onClick={() => (userProfile ? setShowCreateBookingDialog({ court: court.id, date: findFreeSlot(court.id, moment(date).startOf("day").hour(parseInt(hour, 10)).toISOString()) }) : onShowLoginDialog())}
                                />
                            ))}
                        </Box>
                    ))}
                </Box>
            )}

            {/* new booking fab */}
            {!embedded && !(userRole === "standard" && courts.length === 0) && (
                <Fab
                    className={styles.newBookingFab}
                    color="primary"
                    variant="extended"
                    onClick={() => (userProfile ? setShowCreateBookingDialog({ date: moment(date).add(60, "minutes").toISOString() }) : onShowLoginDialog())}
                >
                    <AddIcon className={styles.fabExtendedMargin} />
                    <span className={styles.fabExtendedMargin}>{i18n.newBooking}</span>
                </Fab>
            )}

            {/* booking detail dialog */}
            {showBookingDetailDialog && (
                <BookingDetailDialog
                    bookingId={showBookingDetailDialog}
                    show={!!showBookingDetailDialog}
                    onClose={() => setShowBookingDetailDialog(null)}
                    onCreateNewBooking={(template) => { setShowCreateBookingDialog({ template }); setShowBookingDetailDialog(null); }}
                    getCompanyBookings={getCompanyBookings}
                />
            )}

            {/* create booking dialog */}
            <CreateBookingDialog
                defaultCourt={showCreateBookingDialog?.court}
                defaultStartDate={showCreateBookingDialog?.date}
                show={!!showCreateBookingDialog}
                template={showCreateBookingDialog?.template}
                userProfile={userProfile}
                onClose={() => setShowCreateBookingDialog(null)}
                getCompanyBookings={getCompanyBookings}
            />

            {/* company exclusive booking filter dialogs */}
            <CalendarFilterDialog
                clubId={club?.id}
                show={showCalendarFilterDialog}
                onClose={() => setShowCalendarFilterDialog(false)}
                onReset={reset}
                onSetReset={setReset}
                companyExclusiveBookingFilters={companyExclusiveBookingFilters}
                onSetCompanyExclusiveBookingFilters={setCompanyExclusiveBookingFilters}
                onRefreshCompanyBooking={getCompanyBookings}
            />
        </>
    );
};

export default CalendarView;
